import * as THREE from 'three';
import { stylize } from './stylize.js';

/**
 * A few small flocks wheeling over the player by day. One instanced mesh: each bird is a
 * real little body (head, beak, tapered body, fanned tail) with two-jointed wings, and the
 * wingbeat runs in the vertex shader, so a flock costs one draw call. Birds flap in bursts
 * and glide in between with their wings held in a shallow V, and bank into the turn.
 */

/** Forward is +z, up is +y, wings along x. Lengths in world units (a crow-sized bird). */
function birdGeometry() {
  const pos: number[] = [], col: number[] = [];
  const back = [0.16, 0.15, 0.15], belly = [0.36, 0.33, 0.3], wingTop = [0.2, 0.18, 0.17], tip = [0.09, 0.09, 0.1], beak = [0.55, 0.45, 0.25];
  const tri = (a: number[], b: number[], c: number[], ca: number[], cb = ca, cc = ca) => { pos.push(...a, ...b, ...c); col.push(...ca, ...cb, ...cc); };
  // body: an 8-sided spindle from tail root to the head, darker on the back
  const rings: [number, number][] = [[-0.13, 0.012], [-0.08, 0.032], [0.0, 0.045], [0.07, 0.04], [0.12, 0.028], [0.15, 0.03], [0.19, 0.02], [0.215, 0.004]];
  const sides = 8;
  const ring = (z: number, r: number) => Array.from({ length: sides }, (_, i) => { const a = (i / sides) * Math.PI * 2; return [Math.cos(a) * r, Math.sin(a) * r * 0.9 + (z > 0.12 ? 0.012 : 0), z]; });
  const R = rings.map(([z, r]) => ring(z, r));
  const shade = (p: number[]) => (p[1] > 0.005 ? back : belly);
  for (let k = 0; k < R.length - 1; k++) for (let i = 0; i < sides; i++) {
    const j = (i + 1) % sides, a = R[k][i], b = R[k][j], c = R[k + 1][j], d = R[k + 1][i];
    tri(a, c, b, shade(a), shade(c), shade(b)); tri(a, d, c, shade(a), shade(d), shade(c));
  }
  // beak
  const bz = 0.215, bt = [0, 0.012, 0.27];
  tri([-0.008, 0.012, bz], [0.008, 0.012, bz], bt, beak); tri([0.008, 0.012, bz], [0, 0.0, bz], bt, beak); tri([0, 0.0, bz], [-0.008, 0.012, bz], bt, beak);
  // tail: a fan of three feathers, each a pair of triangles (both faces)
  for (const s of [-1, 0, 1]) {
    const root = [s * 0.008, 0.004, -0.12], l = [s * 0.05 - 0.022, 0, -0.25 + Math.abs(s) * 0.015], r = [s * 0.05 + 0.022, 0, -0.25 + Math.abs(s) * 0.015];
    tri(root, l, r, back);
  }
  // wings: root, elbow and fingered tip on each side (drawn double-sided; the shader flaps everything with |x| > 0.035)
  for (const s of [-1, 1]) {
    const lead = [[0.04, 0.008, 0.06], [0.2, 0.012, 0.075], [0.33, 0.006, 0.03], [0.44, 0.0, -0.03]];
    const trail = [[0.04, 0.008, -0.07], [0.2, 0.01, -0.1], [0.32, 0.004, -0.12], [0.4, 0.0, -0.1]];
    const P = (p: number[]) => [p[0] * s, p[1], p[2]];
    for (let k = 0; k < 3; k++) {
      const a = P(lead[k]), b = P(lead[k + 1]), c = P(trail[k + 1]), d = P(trail[k]);
      const ca = k === 2 ? tip : wingTop, cb = k >= 1 ? tip : wingTop;
      if (s > 0) { tri(a, b, c, ca, cb, cb); tri(a, c, d, ca, cb, ca); }
      else { tri(a, c, b, ca, cb, cb); tri(a, d, c, ca, ca, cb); }
    }
    // primary "fingers" at the wingtip
    for (let f = 0; f < 3; f++) {
      const z0 = -0.03 - f * 0.03, x0 = 0.4 - f * 0.012;
      const a = P([x0, 0, z0]), b = P([x0 + 0.07 - f * 0.01, 0, z0 - 0.012]), c = P([x0, 0, z0 - 0.024]);
      tri(a, b, c, tip);
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
  g.computeVertexNormals();
  return g;
}

export class Birds {
  private mesh: THREE.InstancedMesh;
  private birds: { flock: number; off: THREE.Vector3; phase: number }[] = [];
  private flocks: { cx: number; cz: number; r: number; h: number; a: number; w: number }[] = [];
  private m = new THREE.Matrix4(); private q = new THREE.Quaternion(); private e = new THREE.Euler(0, 0, 0, 'YXZ'); private v = new THREE.Vector3(); private s = new THREE.Vector3(1, 1, 1);
  private uTime = { value: 0 };
  constructor(scene: THREE.Scene, count = 14) {
    const g = birdGeometry();
    const phase = new Float32Array(count), speed = new Float32Array(count);
    for (let i = 0; i < count; i++) { phase[i] = Math.random() * 20; speed[i] = 9 + Math.random() * 3; }
    g.setAttribute('aPhase', new THREE.InstancedBufferAttribute(phase, 1));
    g.setAttribute('aSpeed', new THREE.InstancedBufferAttribute(speed, 1));
    const mat = new THREE.MeshLambertMaterial({ vertexColors: true, side: THREE.DoubleSide });
    stylize(mat, { clouds: false });
    const styled = mat.onBeforeCompile, uTime = this.uTime;
    mat.onBeforeCompile = (shader, r) => {
      styled.call(mat, shader, r);
      shader.uniforms.uBirdTime = uTime;
      shader.vertexShader = shader.vertexShader
        .replace('void main() {', 'uniform float uBirdTime;\nattribute float aPhase;\nattribute float aSpeed;\nvoid main() {')
        .replace('#include <begin_vertex>', `#include <begin_vertex>
          {
            float s = abs(position.x);
            if (s > 0.035) {
              // bursts of flapping, then a glide with the wings held in a shallow V
              float flapping = smoothstep(0.35, 0.75, 0.5 + 0.5 * sin(uBirdTime * 0.45 + aPhase * 1.7));
              float beat = sin(uBirdTime * aSpeed + aPhase);
              float a1 = mix(0.16, beat * 0.62, flapping), a2 = a1 + mix(0.06, beat * 0.35 - 0.1, flapping);
              float inner = clamp(s - 0.035, 0.0, 0.17), outer = max(s - 0.205, 0.0);
              float xx = 0.035 + inner * cos(a1) + outer * cos(a2);
              float yy = inner * sin(a1) + outer * sin(a2);
              transformed.x = sign(position.x) * xx;
              transformed.y += yy;
              // the body bobs against the wingbeat
            } else {
              float flapping = smoothstep(0.35, 0.75, 0.5 + 0.5 * sin(uBirdTime * 0.45 + aPhase * 1.7));
              transformed.y -= sin(uBirdTime * aSpeed + aPhase) * 0.012 * flapping;
            }
          }`);
    };
    mat.customProgramCacheKey = () => 'sf-birds';
    this.mesh = new THREE.InstancedMesh(g, mat, count);
    this.mesh.frustumCulled = false;
    this.mesh.castShadow = false;
    scene.add(this.mesh);
    for (let f = 0; f < 3; f++) this.flocks.push({ cx: 0, cz: 0, r: 10 + f * 6, h: 11 + f * 3, a: Math.random() * 6, w: (f % 2 ? 1 : -1) * (0.12 + f * 0.03) });
    for (let i = 0; i < count; i++) this.birds.push({ flock: i % 3, off: new THREE.Vector3((Math.random() - 0.5) * 3, (Math.random() - 0.5) * 1.5, (Math.random() - 0.5) * 3), phase: phase[i] });
  }
  update(dt: number, time: number, cx: number, cz: number, visible: number) {
    this.mesh.visible = visible > 0.05;
    if (!this.mesh.visible) return;
    this.uTime.value = time;
    for (const f of this.flocks) { f.a += f.w * dt; f.cx += (cx - f.cx) * Math.min(1, dt * 0.2); f.cz += (cz - f.cz) * Math.min(1, dt * 0.2); }
    this.birds.forEach((b, i) => {
      const f = this.flocks[b.flock];
      const a = f.a + b.off.x * 0.05, bob = Math.sin(time * 0.7 + b.phase);
      const x = f.cx + Math.cos(a) * f.r + b.off.x, z = f.cz + Math.sin(a) * f.r + b.off.z, y = f.h + b.off.y + bob * 0.6;
      const heading = Math.atan2(-Math.sin(a) * Math.sign(f.w), Math.cos(a) * Math.sign(f.w));
      // bank into the circle, nose up a touch when climbing
      this.e.set(-Math.cos(time * 0.7 + b.phase) * 0.12, heading, -Math.sign(f.w) * 0.3);
      this.q.setFromEuler(this.e);
      this.v.set(x, y, z); this.s.setScalar(1.25);
      this.mesh.setMatrixAt(i, this.m.compose(this.v, this.q, this.s));
    });
    this.mesh.instanceMatrix.needsUpdate = true;
  }
}
